/**
 * WorkflowEditor (SPEC PX-4 — workflow-builder, R2).
 *
 * The authoring surface: drag actions from the palette onto the canvas, edit each
 * node's config in a schema-driven side panel, wire branches / split /
 * wait_for_event, and save. Save = POST (create, active:false) / PUT (update) via
 * the existing endpoints (auto-validate; a 422 surfaces inline, state preserved).
 * Activation is a separate explicit PUT {active:true}; for any definition with a
 * send_message / send_email / notify_operator action it surfaces an EXPLICIT
 * confirm dialog FIRST (Rule 0 — the engine send path is NOT fail-closed).
 *
 * The graph<->JSON compile is `lib/workflow-graph.ts` graphToDefinition — the
 * canvas never auto-activates and never calls on_event.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { toast } from 'sonner';
import { Power, PowerOff, Save, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { WorkflowCanvas } from './WorkflowCanvas';
import { PalettePanel } from './PalettePanel';
import { NodeConfigPanel } from './NodeConfigPanel';
import {
  addActionNode,
  deleteActionNode,
  emptyGraph,
  graphHasSendBearingAction,
  graphToDefinition,
  setNodeConfig,
  setNodeType,
} from '@/lib/workflow-graph';
import {
  useCreateWorkflow,
  useDeleteWorkflow,
  useUpdateWorkflow,
  useWorkflow,
  useWorkflowMetadata,
} from '@/lib/api/hooks/useWorkflows';
import { definitionToGraph } from '@/lib/workflow-graph';
import type { WorkflowCreate, WorkflowGraph } from '@/types/workflow';
import { TRIGGER_NODE_ID } from '@/types/workflow';

interface WorkflowEditorProps {
  /** Existing workflow id to edit, or undefined for a new draft. */
  workflowId?: string;
  /** Initial active state (existing workflows). */
  initialActive?: boolean;
  onClose: () => void;
  onSaved?: (id: string) => void;
  onDeleted?: () => void;
}

export function WorkflowEditor({
  workflowId,
  initialActive = false,
  onClose,
  onSaved,
  onDeleted,
}: WorkflowEditorProps) {
  const { data: metadata } = useWorkflowMetadata();
  const { data: existing, isLoading: loadingExisting } = useWorkflow(workflowId || '');
  const createMutation = useCreateWorkflow();
  const updateMutation = useUpdateWorkflow();
  const deleteMutation = useDeleteWorkflow();

  const [graph, setGraph] = useState<WorkflowGraph>(() => emptyGraph(''));
  const [name, setName] = useState('');
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(TRIGGER_NODE_ID);
  const [savedId, setSavedId] = useState<string | undefined>(workflowId);
  const [active, setActive] = useState(initialActive);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const [confirmActivate, setConfirmActivate] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Load an existing definition into the canvas exactly once.
  useEffect(() => {
    if (existing && workflowId && loadedFor !== workflowId) {
      setGraph(definitionToGraph(existing));
      setName(existing.name);
      setActive(!!existing.active);
      setSavedId(existing.id);
      setLoadedFor(workflowId);
    }
  }, [existing, workflowId, loadedFor]);

  const triggerTypes = useMemo(
    () => (metadata?.triggers ?? []).map((t) => t.type),
    [metadata]
  );

  const actionNodes = graph.nodes.filter((nd) => nd.kind === 'action');
  const selectedNode = graph.nodes.find((nd) => nd.id === selectedNodeId) || null;
  const sendBearing = graphHasSendBearingAction(graph);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  const onDragEnd = (e: DragEndEvent) => {
    const { active: dragged, over } = e;
    const type = dragged.data.current?.paletteActionType as string | undefined;
    if (type && over?.id === 'workflow-canvas') {
      // Append after the selected action node (or at the end).
      const after = selectedNode && selectedNode.kind === 'action' ? selectedNode.id : null;
      const next = addActionNode(graph, type, after);
      setGraph(next);
      // Select the newly-added node (last action node after renumber).
      const newActions = next.nodes.filter((nd) => nd.kind === 'action');
      const target = after
        ? newActions[Math.min(actionNodes.findIndex((n) => n.id === after) + 1, newActions.length - 1)]
        : newActions[newActions.length - 1];
      if (target) setSelectedNodeId(target.id);
    }
  };

  const handleConfigChange = (nodeId: string, config: Record<string, unknown>) => {
    const next = setNodeConfig(graph, nodeId, config);
    setGraph(next);
    // keep selection on the same logical node (renumber may re-id it). The
    // trigger node id is stable; action nodes re-id to action-<i> in order.
    if (nodeId === TRIGGER_NODE_ID) return;
  };

  const handleTypeChange = (nodeId: string, type: string) => {
    setGraph(setNodeType(graph, nodeId, type));
  };

  const handleDeleteNode = (nodeId: string) => {
    setGraph(deleteActionNode(graph, nodeId));
    setSelectedNodeId(TRIGGER_NODE_ID);
  };

  const buildBody = () => {
    const def = graphToDefinition({ ...graph, name });
    return def;
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Please enter a workflow name');
      return;
    }
    const def = buildBody();
    try {
      if (savedId) {
        await updateMutation.mutateAsync({
          id: savedId,
          data: {
            name: def.name,
            trigger: def.trigger,
            actions: def.actions,
            re_enrollment: def.re_enrollment,
            goal_condition: def.goal_condition ?? null,
          },
        });
        toast.success('Workflow saved');
        onSaved?.(savedId);
      } else {
        // First create: ALWAYS active:false (dead-by-default).
        const body: WorkflowCreate = {
          name: def.name,
          trigger: def.trigger,
          actions: def.actions,
          active: false,
          re_enrollment: def.re_enrollment,
          goal_condition: def.goal_condition ?? null,
        };
        const created = await createMutation.mutateAsync(body);
        setSavedId(created.id);
        setLoadedFor(created.id);
        setActive(false);
        toast.success('Workflow saved');
        onSaved?.(created.id);
      }
    } catch (err) {
      // Surface the validator's 422 detail; canvas state is preserved.
      toast.error(err instanceof Error ? err.message : 'Failed to save workflow');
    }
  };

  const doSetActive = async (next: boolean) => {
    if (!savedId) {
      toast.error('Save the workflow before activating');
      return;
    }
    try {
      await updateMutation.mutateAsync({ id: savedId, data: { active: next } });
      setActive(next);
      toast.success(next ? 'Workflow activated' : 'Workflow deactivated');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update workflow');
    }
  };

  const handleToggleActive = () => {
    if (active) {
      // Deactivation needs no confirm.
      doSetActive(false);
      return;
    }
    // Activation: confirm FIRST for any send-bearing definition (Rule 0).
    if (sendBearing) {
      setConfirmActivate(true);
      return;
    }
    doSetActive(true);
  };

  const handleDelete = async () => {
    if (!savedId) {
      onClose();
      return;
    }
    try {
      await deleteMutation.mutateAsync(savedId);
      toast.success('Workflow deleted');
      setConfirmDelete(false);
      onDeleted?.();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete workflow');
    }
  };

  const saving = createMutation.isPending || updateMutation.isPending;

  if (workflowId && loadingExisting && loadedFor !== workflowId) {
    return <div className="p-6 text-sm text-muted-foreground">Loading workflow…</div>;
  }

  return (
    <DndContext sensors={sensors} onDragEnd={onDragEnd}>
      <div className="h-full flex flex-col overflow-hidden">
        {/* Editor toolbar */}
        <div className="border-b px-4 py-3 flex items-center gap-3">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Workflow name"
            className="max-w-xs"
            data-testid="workflow-name-input"
          />
          {savedId &&
            (active ? (
              <Badge className="bg-green-100 text-green-800">Active</Badge>
            ) : (
              <Badge variant="secondary">Inactive</Badge>
            ))}
          <div className="ml-auto flex items-center gap-2">
            <Button onClick={handleSave} disabled={saving} data-testid="save-workflow-btn">
              <Save className="h-4 w-4 mr-2" />
              Save
            </Button>
            {savedId && (
              <Button
                variant={active ? 'default' : 'outline'}
                onClick={handleToggleActive}
                data-testid="activate-toggle-btn"
                title={active ? 'Deactivate' : 'Activate'}
              >
                {active ? <Power className="h-4 w-4 mr-2" /> : <PowerOff className="h-4 w-4 mr-2" />}
                {active ? 'Active' : 'Activate'}
              </Button>
            )}
            {savedId && (
              <Button
                variant="ghost"
                size="icon"
                className="text-destructive"
                onClick={() => setConfirmDelete(true)}
                data-testid="delete-workflow-btn"
                title="Delete workflow"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
            <Button variant="ghost" size="icon" onClick={onClose} title="Close">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="flex-1 flex overflow-hidden">
          {/* Canvas */}
          <div className="flex-1 overflow-hidden p-3">
            <WorkflowCanvas
              graph={graph}
              editable
              selectedNodeId={selectedNodeId}
              onSelectNode={setSelectedNodeId}
            />
          </div>

          {/* Config side panel */}
          <div className="w-80 shrink-0 border-l overflow-auto p-3" data-testid="config-panel-column">
            {selectedNode ? (
              <NodeConfigPanel
                node={selectedNode}
                metadata={metadata}
                actionNodes={actionNodes}
                triggerTypes={triggerTypes}
                onChange={handleConfigChange}
                onChangeType={handleTypeChange}
                onDelete={handleDeleteNode}
              />
            ) : (
              <p className="text-sm text-muted-foreground">Select a node to edit it.</p>
            )}
          </div>

          {/* Draggable palette */}
          <PalettePanel metadata={metadata} draggable />
        </div>
      </div>

      {/* Send-bearing activation confirm (Rule 0 — AC8). */}
      <AlertDialog open={confirmActivate} onOpenChange={(o) => !o && setConfirmActivate(false)}>
        <AlertDialogContent data-testid="activate-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Activate this workflow?</AlertDialogTitle>
            <AlertDialogDescription>
              This workflow contains a send / notify action. Once active, it will send
              REAL messages to contacts on its next trigger. The engine has no extra
              send gate — activating is the only lock. Are you sure you want to activate?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="activate-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="activate-confirm-ok"
              onClick={() => {
                setConfirmActivate(false);
                doSetActive(true);
              }}
            >
              Yes, activate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirm. */}
      <AlertDialog open={confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(false)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this workflow?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes the workflow definition. Running executions are
              unaffected, but no new contacts will be enrolled.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DndContext>
  );
}
